import { ReactChild, ReactChildren, useState } from 'react';

import {
  faChevronUp,
  faExchange,
  faUsers
} from '@fortawesome/pro-light-svg-icons';
import { faCircle, faUserCrown } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { useSelector } from 'hooks';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Duty, OpenDuty } from '../types';

import { handleSelfMatchRequest } from 'RestAPIs/self-match/duties';

import { FontAwesomeSvgIcon, showSnackbar, Tooltip } from 'components/shared';

import getTooltipContent from './TooltipContent';

const OpenRequestDutySyles = styled.div`
  position: relative;

  .subscribe-container {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.9);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    visibility: hidden;
  }

  &.has-subscription-action:hover {
    cursor: pointer;

    .subscribe-container {
      visibility: visible;
    }
  }

  .function span {
    font-size: 0;
  }
`;

type Props = {
  duty: OpenDuty;
  children: ReactChild | ReactChildren;
  isGroupedByFunction: boolean;
  portal: string;
  className: string;
  updateDuty: (duty: Duty) => void;
};

export default function OpenRequestDuty({
  duty,
  children,
  isGroupedByFunction,
  portal,
  className,
  updateDuty
}: Props) {
  const { t } = useTranslation('common');
  const { employee } = useSelector((state) => state.userDetails);
  // eslint-disable-next-line max-len
  const {
    hasEmployeePortalSelfMatching,
    hasEmployeePortalSelfRosteringSubscribe,
    hasEmployeePortalSelfRosteringConnect
  } = useSelector((state) => state.grants);
  const {
    function: func,
    requestId,
    canConnect: _canConnect,
    canSubscribe: _canSubscribe,
    canUnsubscribe: _canUnsubscribe
  } = duty;
  // const isNotFromBaseplan = !isFromBasePlan;
  const isNotFromBaseplan = false;
  const hasTopRighIcons = isNotFromBaseplan;
  const { name: functionName, colorCode: functionColor } = func || {};

  const canConnect = Boolean(
    hasEmployeePortalSelfMatching &&
      hasEmployeePortalSelfRosteringConnect &&
      _canConnect
  );
  const [canSubscribe, setCanSubscribe] = useState(
    Boolean(
      hasEmployeePortalSelfMatching &&
        hasEmployeePortalSelfRosteringSubscribe &&
        _canSubscribe
    )
  );
  const [canUnsubscribe, setCanUnsubscribe] = useState(
    Boolean(_canUnsubscribe)
  );

  const handleSelfMatchClick = () => {
    handleSelfMatchRequest(
      requestId,
      canConnect || canSubscribe ? 'subscribe' : 'reject'
    ).then(
      () => {
        if (canConnect) {
          const {
            empId,
            nr,
            nameCompound,
            nameCompoundSurname,
            nameInformal,
            nameSurnameCompound,
            comPhone,
            comPhoneAlt,
            comSms,
            comEmail
          } = employee;
          updateDuty({
            ...duty,
            isOpen: false,
            dutyLineId: Math.random(),
            isDutyHourTypeChanged: false,
            assignedEmployee: {
              id: empId,
              number: nr,
              nameCompound,
              nameCompoundSurname,
              nameInformal,
              nameSurnameCompound,
              phoneNumber: comPhone,
              alternativePhoneNumber: comPhoneAlt,
              smsNumber: comSms,
              email: comEmail
            }
          });
        } else if (canSubscribe) {
          const {
            empId,
            nr,
            nameCompound,
            nameCompoundSurname,
            nameInformal,
            nameSurnameCompound,
            comPhone,
            comPhoneAlt,
            comSms,
            comEmail
          } = employee;
          updateDuty({
            ...duty,
            subscribedEmployees: [
              ...(duty.subscribedEmployees || []),
              {
                id: empId,
                number: nr,
                nameCompound,
                nameCompoundSurname,
                nameInformal,
                nameSurnameCompound,
                phoneNumber: comPhone,
                alternativePhoneNumber: comPhoneAlt,
                smsNumber: comSms,
                email: comEmail
              }
            ]
          });
        } else {
          updateDuty({
            ...duty,
            subscribedEmployees: duty.subscribedEmployees?.filter(
              ({ id }) => id !== employee.empId
            )
          });
        }

        setCanSubscribe(!canSubscribe);
        setCanUnsubscribe(canSubscribe);
        showSnackbar({
          message: canConnect
            ? t('connectSuccessfull')
            : canSubscribe
            ? t('subscribeSuccessfull')
            : t('unsubscribeSuccessfull'),
          severity: 'success'
        });
      },
      () => {
        showSnackbar({
          message: canConnect
            ? t('connectFailed')
            : canSubscribe
            ? t('subscribeFailed')
            : t('unsubscribeFailed'),
          severity: 'error'
        });
      }
    );
  };

  return (
    <Tooltip
      title={getTooltipContent(duty, portal)}
      arrow
      enterDelay={300}
      enterNextDelay={300}
      placement="right"
      disableInteractive
    >
      <OpenRequestDutySyles
        className={classNames(className, 'duty', 'open', {
          'has-subscription-action':
            canConnect || canSubscribe || canUnsubscribe
        })}
        onClick={
          canConnect || canSubscribe || canUnsubscribe
            ? handleSelfMatchClick
            : undefined
        }
      >
        <div className="duty-header">
          {canConnect
            ? t('connect')
            : canSubscribe
            ? t('subscribe')
            : canUnsubscribe
            ? t('subscribed')
            : t('open')}
        </div>
        {children}
        {!isGroupedByFunction && functionName && (
          <div className="function d-flex v-center">
            {functionColor && (
              <span style={{ color: functionColor }}>
                <FontAwesomeSvgIcon
                  color="inherit"
                  icon={faCircle}
                  fontSize="small"
                  className="function-color"
                />
              </span>
            )}
            <small>{functionName}</small>
          </div>
        )}
        {hasTopRighIcons && (
          <div className="top-right-icons">
            {isNotFromBaseplan && (
              <FontAwesomeSvgIcon icon={faChevronUp} fontSize="small" />
            )}
          </div>
        )}
        {(canConnect || canSubscribe || canUnsubscribe) && (
          <div className="subscribe-container">
            <h4>
              {t(
                canConnect
                  ? 'clickToConnect'
                  : canSubscribe
                  ? 'clickToSubscribe'
                  : 'clickToUnsubscribe'
              )}
            </h4>
          </div>
        )}
      </OpenRequestDutySyles>
    </Tooltip>
  );
}

export const hasIcons = ({
  sentToFlex,
  isSentForSelfMatch,
  subscribedEmployees,
  preferredEmployees
}: OpenDuty) =>
  Boolean(
    sentToFlex ||
      isSentForSelfMatch ||
      subscribedEmployees?.length ||
      preferredEmployees?.length
  );

export const OpenRequestDutyIcons = (
  {
    sentToFlex,
    isSentForSelfMatch,
    subscribedEmployees,
    preferredEmployees
  }: OpenDuty,
  portal: string
) => {
  const isRosterPortal = portal === 'roster';
  const hasSubscribers = Boolean(subscribedEmployees?.length);
  const hasPreferredEmployees =
    isRosterPortal && Boolean(preferredEmployees?.length);
  const showSentToFlex = sentToFlex;

  return (
    <>
      {(isSentForSelfMatch || hasSubscribers) && (
        <span className={classNames({ 'has-subscribers': hasSubscribers })}>
          <FontAwesomeSvgIcon icon={faUsers} fontSize="small" />
          {hasSubscribers && (
            <span className="cnt">{subscribedEmployees?.length}</span>
          )}
        </span>
      )}
      {showSentToFlex && (
        <FontAwesomeSvgIcon icon={faExchange} fontSize="small" />
      )}
      {hasPreferredEmployees && (
        <FontAwesomeSvgIcon icon={faUserCrown} fontSize="small" />
      )}
    </>
  );
};
